/**
 * Platform incidents (`/api/admin/v2/incidents`), Operations > Incidents.
 * `system.config.view` reads, `system.config.edit` changes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useHrApi } from '../hr/people';

const BASE = '/api/admin/v2/incidents';

export type IncidentSeverity = 'sev1' | 'sev2' | 'sev3';
export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';
export type IncidentFilter = 'open' | 'resolved' | 'all';

/** Datetimes are ISO UTC strings. */
export interface Incident {
  id: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  area: string;
  startedAt: string;
  resolvedAt: string | null;
  impact: string;
  cause: string;
  fix: string;
  followUp: string;
  owner: string;
  link: string;
  createdAt: string;
  updatedAt: string;
}

export type IncidentInput = Partial<Incident>;

export const incidentKeys = { all: ['incidents'] as const };

export const useIncidentsQuery = (status: IncidentFilter) => {
  const api = useHrApi(BASE);
  return useQuery({
    queryKey: [...incidentKeys.all, status],
    queryFn: async () => (await api<{ items: Incident[] }>(`?status=${status}`)).items ?? [],
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
  });
};

function useIncidentMutation<TIn, TOut>(fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(BASE);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TIn) => fn(api, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: incidentKeys.all }),
  });
}

/** With an `id` this PATCHes, otherwise it POSTs a new incident. */
export const useSaveIncidentMutation = () =>
  useIncidentMutation((api, { id, ...body }: IncidentInput) =>
    id ? api<Incident>(`/${encodeURIComponent(id)}`, 'PATCH', body) : api<Incident>('', 'POST', body),
  );

export const useDeleteIncidentMutation = () =>
  useIncidentMutation((api, id: string) =>
    api<{ id: string; deleted: boolean }>(`/${encodeURIComponent(id)}`, 'DELETE'),
  );
